import { useState, useEffect, useCallback } from 'react';
import {
  AppWindow,
  Search,
  ExternalLink,
  Users,
  Clock,
  Mail,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/utils';
import { getPublishedApps, type AppData, type ListAppsParams } from '@/api/apps';

export default function AppsPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [apps, setApps] = useState<AppData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<AppData | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch apps
  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const params: ListAppsParams = {
        page,
        pageSize,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      const result = await getPublishedApps(params);
      setApps(result?.items ?? []);
      setTotal(result?.total ?? 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load apps';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, toast, t.common.error]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const totalPages = Math.ceil(total / pageSize);

  if (loading && apps.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.admin.publishedAppsTitle}</h1>
          <p className="text-gray-500 mt-1">
            {t.admin.publishedAppsDesc.replace('{count}', String(total))}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t.admin.searchApps}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Apps grid */}
      {apps.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AppWindow className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">{t.admin.noApps}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <Card
                key={app.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedApp(app)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center flex-shrink-0">
                      {app.iconUrl ? (
                        <img src={app.iconUrl} alt={app.name} className="w-8 h-8 rounded" />
                      ) : (
                        <AppWindow className="w-6 h-6 text-primary-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{app.name}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {app.description}
                      </p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="mt-4 flex flex-wrap gap-1">
                    {app.categoryTags?.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="default">{tag}</Badge>
                    ))}
                  </div>

                  {/* Meta info */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {t.admin.groupCount.replace('{count}', String(app.allowedGroups.length))}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      v{app.version}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-500">
                {t.admin.showingApps
                  .replace('{from}', String((page - 1) * pageSize + 1))
                  .replace('{to}', String(Math.min(page * pageSize, total)))
                  .replace('{total}', String(total))}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-700">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* App Detail Modal */}
      <Modal
        isOpen={selectedApp !== null}
        onClose={() => setSelectedApp(null)}
        title={selectedApp?.name}
        size="lg"
      >
        {selectedApp && (
          <div className="space-y-4">
            <p className="text-gray-600">{selectedApp.description}</p>

            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <span className="text-sm text-gray-500">{t.common.version}</span>
                <p className="font-medium text-gray-900">v{selectedApp.version}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.admin.submittedBy}</span>
                <p className="font-medium text-gray-900">{selectedApp.creatorEmail}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.common.createdAt}</span>
                <p className="font-medium text-gray-900">{formatDateTime(selectedApp.createdAt)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.common.updatedAt}</span>
                <p className="font-medium text-gray-900">{formatDateTime(selectedApp.updatedAt)}</p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t.admin.appLink}</h4>
              <a
                href={selectedApp.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary-600 hover:underline"
              >
                {selectedApp.link}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t.creator.categoryTags}</h4>
              <div className="flex flex-wrap gap-2">
                {selectedApp.categoryTags?.map((tag) => (
                  <Badge key={tag} variant="default">{tag}</Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t.admin.visibleScope}</h4>
              <div className="flex flex-wrap gap-2">
                {selectedApp.allowedGroups?.map((group) => (
                  <span
                    key={group}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-full"
                  >
                    <Users className="w-4 h-4" />
                    {group}
                  </span>
                ))}
              </div>
            </div>

            {selectedApp.allowedEmails?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t.creator.addIndividuals}</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedApp.allowedEmails?.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-full"
                    >
                      <Mail className="w-4 h-4" />
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button variant="outline" onClick={() => setSelectedApp(null)}>
                {t.common.close}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
